import React, { useRef, useEffect, createContext, useContext, useState } from 'react';
import { Box, Typography } from '@mui/material';

// Context pour gérer les annonces aux lecteurs d'écran
const AccessibilityContext = createContext();

// Composant pour les annonces aux lecteurs d'écran
const ScreenReaderAnnouncement = ({ 
  message, 
  priority = 'polite',
  clearAfterDelay = true,
  delay = 5000 
}) => {
  const [currentMessage, setCurrentMessage] = useState(message);
  
  useEffect(() => {
    setCurrentMessage(message);
    
    if (clearAfterDelay && message) {
      const timer = setTimeout(() => {
        setCurrentMessage('');
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [message, clearAfterDelay, delay]);

  if (!currentMessage) return null;

  return (
    <Box
      component="div"
      role="status"
      aria-live={priority}
      aria-atomic="true"
      sx={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(1px, 1px, 1px, 1px)',
        clipPath: 'inset(50%)',
        border: 0,
        padding: 0,
        margin: '-1px'
      }}
    >
      {currentMessage}
    </Box>
  );
};

// Hook pour annoncer des messages aux lecteurs d'écran
const useScreenReader = () => {
  const [announcement, setAnnouncement] = useState('');
  const [priority, setPriority] = useState('polite');
  
  const announce = (message, messagePriority = 'polite') => {
    setAnnouncement(''); // Clear first to ensure re-announcement
    setTimeout(() => {
      setAnnouncement(message);
      setPriority(messagePriority);
    }, 100);
  };
  
  const clear = () => {
    setAnnouncement('');
  };
  
  return {
    announce,
    clear,
    announcement,
    priority,
    AnnouncementComponent: () => (
      <ScreenReaderAnnouncement 
        message={announcement} 
        priority={priority}
      />
    )
  };
};

// Composant pour les éléments cachés visuellement mais accessibles aux lecteurs d'écran
const VisuallyHidden = ({ children, component = 'span', ...props }) => {
  return (
    <Box
      component={component}
      sx={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(1px, 1px, 1px, 1px)',
        clipPath: 'inset(50%)',
        border: 0,
        padding: 0,
        margin: '-1px'
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Hook pour la gestion du focus
const useFocusManagement = () => {
  const focusElement = (element) => {
    if (element && element.focus) {
      element.focus();
    }
  };
  
  const focusFromRef = (ref) => {
    if (ref && ref.current) {
      focusElement(ref.current);
    }
  };
  
  const focusById = (id) => {
    const element = document.getElementById(id);
    focusElement(element);
  };
  
  const focusFirstFocusableElement = (container) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusElement(focusableElements[0]);
    }
  };
  
  const trapFocus = (container) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    container.addEventListener('keydown', handleKeyDown);
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  };
  
  return {
    focusElement,
    focusFromRef,
    focusById,
    focusFirstFocusableElement,
    trapFocus
  };
};

// Composant pour les liens de saut (skip links)
const SkipLink = ({ 
  href, 
  children = 'Aller au contenu principal',
  ...props 
}) => {
  return (
    <Box
      component="a"
      href={href}
      sx={{
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        zIndex: 9999,
        padding: '8px 16px',
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        textDecoration: 'none',
        borderRadius: '0 0 4px 4px',
        fontWeight: 'bold',
        '&:focus': {
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'auto',
          height: 'auto',
          overflow: 'visible'
        }
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Composant pour les descriptions d'accessibilité
const AccessibilityDescription = ({ 
  id, 
  children, 
  visible = false 
}) => {
  if (visible) {
    return (
      <Typography 
        id={id} 
        variant="body2" 
        color="text.secondary"
        sx={{ mt: 1 }}
      >
        {children}
      </Typography>
    );
  }
  
  return (
    <VisuallyHidden id={id}>
      {children}
    </VisuallyHidden>
  );
};

// Composant pour les alertes accessibles
const AccessibleAlert = ({ 
  severity = 'info',
  title,
  children,
  onClose,
  autoHideDuration,
  announce = true,
  ...props 
}) => {
  const { announce: announceMessage } = useScreenReader();
  
  useEffect(() => {
    if (announce && (title || children)) {
      const message = title ? `${title}. ${children}` : children;
      announceMessage(message, severity === 'error' ? 'assertive' : 'polite');
    }
  }, [announce, title, children, severity, announceMessage]);
  
  useEffect(() => {
    if (autoHideDuration && onClose) {
      const timer = setTimeout(onClose, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [autoHideDuration, onClose]);
  
  return (
    <Box
      role="alert"
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      {...props}
    >
      {title && (
        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
};

// Provider pour l'accessibilité
const AccessibilityProvider = ({ children }) => {
  const screenReader = useScreenReader();
  const focusManagement = useFocusManagement();
  
  const value = {
    ...screenReader,
    ...focusManagement
  };
  
  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      <screenReader.AnnouncementComponent />
    </AccessibilityContext.Provider>
  );
};

// Hook pour utiliser le contexte d'accessibilité
const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

// Hook pour détecter les préférences d'accessibilité
const useAccessibilityPreferences = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [prefersHighContrast, setPrefersHighContrast] = useState(false);
  const [prefersLargeText, setPrefersLargeText] = useState(false);
  
  useEffect(() => {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
    const largeTextQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    setPrefersReducedMotion(reducedMotionQuery.matches);
    setPrefersHighContrast(highContrastQuery.matches);
    setPrefersLargeText(largeTextQuery.matches);
    
    const handleReducedMotionChange = (e) => setPrefersReducedMotion(e.matches);
    const handleHighContrastChange = (e) => setPrefersHighContrast(e.matches);
    const handleLargeTextChange = (e) => setPrefersLargeText(e.matches);
    
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
    highContrastQuery.addEventListener('change', handleHighContrastChange);
    largeTextQuery.addEventListener('change', handleLargeTextChange);
    
    return () => {
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      highContrastQuery.removeEventListener('change', handleHighContrastChange);
      largeTextQuery.removeEventListener('change', handleLargeTextChange);
    };
  }, []);
  
  return {
    prefersReducedMotion,
    prefersHighContrast,
    prefersLargeText
  };
};

export {
  ScreenReaderAnnouncement,
  useScreenReader,
  VisuallyHidden,
  useFocusManagement,
  SkipLink,
  AccessibilityDescription,
  AccessibleAlert,
  AccessibilityProvider,
  useAccessibility,
  useAccessibilityPreferences
};